import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subscription } from 'rxjs';
import { DeliveryTrackingComponent } from '../delivery-tracking.component';
import { DeliveryTrack } from '../delivery-tracking.models';
import {
  DEMO_ORDER,
  DEMO_PHASES,
  DEMO_SPEEDS,
  DeliveryTrackingSimulator,
  DemoPhase,
} from './delivery-tracking-simulator';

interface DemoLine {
  quantity: number;
  name: string;
  modifiers: string[];
  total: number;
}

/**
 * /order-tracking-demo: the order status page for a sample delivery order, driven
 * by a simulated tracker. No login, no backend calls, sample data only.
 */
@Component({
  selector: 'app-order-tracking-demo',
  standalone: true,
  imports: [CommonModule, MatToolbarModule, MatDividerModule, DeliveryTrackingComponent],
  templateUrl: './order-tracking-demo.component.html',
  // Reuses the order status page styles so the demo looks like the real page.
  styleUrls: ['../../order-status.component.scss', './order-tracking-demo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderTrackingDemoComponent implements OnInit, OnDestroy {
  readonly order = DEMO_ORDER;
  readonly phases = DEMO_PHASES;
  readonly speeds = DEMO_SPEEDS;
  readonly initials = 'HPC';

  readonly lines: DemoLine[] = [
    { quantity: 2, name: 'Large Margherita', modifiers: ['Extra basil'], total: 42.0 },
    { quantity: 1, name: 'Large Pepperoni', modifiers: [], total: 23.99 },
    { quantity: 1, name: 'Caesar Salad', modifiers: ['Dressing on the side'], total: 12.0 },
  ];
  readonly subTotal = 77.99;
  readonly tax = 5.46;
  readonly deliveryFee = 2.95;
  readonly total = 86.4;

  readonly simulator = new DeliveryTrackingSimulator();
  track: DeliveryTrack = this.simulator.track;
  controlsOpen = true;

  private subscription: Subscription;

  constructor(private cdr: ChangeDetectorRef) {}

  get phase(): DemoPhase {
    return this.simulator.phase;
  }

  get playing(): boolean {
    return this.simulator.playing;
  }

  get itemCount(): number {
    return this.lines.reduce((sum, line) => sum + line.quantity, 0);
  }

  ngOnInit(): void {
    this.subscription = this.simulator.track$.subscribe((track) => {
      this.track = track;
      this.cdr.markForCheck();
    });
    this.simulator.play();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.simulator.destroy();
  }

  togglePlay(): void {
    if (this.simulator.playing) {
      this.simulator.pause();
    } else {
      this.simulator.play();
    }
  }

  restart(): void {
    this.simulator.restart();
    this.simulator.play();
  }

  jump(phase: DemoPhase): void {
    this.simulator.jumpTo(phase);
    // Carry on from the chosen state, unless it is an end state.
    if (!this.simulator.finished) {
      this.simulator.play();
    }
  }

  setSpeed(value: string): void {
    this.simulator.setSpeed(Number(value));
  }

  setLiveMap(enabled: boolean): void {
    this.simulator.setLiveMap(enabled);
  }

  setCourier(enabled: boolean): void {
    this.simulator.setCourier(enabled);
  }

  trackPhase(_: number, phase: { key: DemoPhase }): string {
    return phase.key;
  }
}
